// D&D 5e Monster Editor Component
// Uses Bootstrap styling consistent with existing PF2e editor

import * as data from './data'
import { createMonster, defaultAttack, defaultTrait, defaultAction, defaultBonusAction, defaultReaction, defaultSpeed } from './monster'

// Export data for use in templates
export const dnd5eData = {
  sizes: data.CREATURE_SIZE,
  types: data.CREATURE_TYPE,
  alignments: data.ALIGNMENT,
  stats: data.STATS,
  statNames: data.STAT_NAMES,
  skills: data.SKILLS,
  damageTypes: data.DAMAGE_TYPES,
  conditions: data.CONDITIONS,
  dice: data.DICE_SELECT,
  crOptions: data.CR_SELECT,
  movementTypes: data.MOVEMENT_TYPES,
  attackTypes: data.ATTACK_TYPES,
  rechargeOptions: data.RECHARGE_TIMES,
}

// Helper functions for computed values
export function getModifier(score) {
  return data.getModifier(score)
}

export function formatModifier(mod) {
  return data.formatModifier(mod)
}

export function getCrData(cr) {
  return data.getCrByString(cr)
}

export function getAvgHp(hd, die, modifier) {
  return data.avgRoll(hd, die) + modifier
}

export function getDieName(dieValue) {
  return data.DIE_LOOKUP[dieValue] || 'd8'
}

export function getHdForSize(size) {
  return data.HD_FOR_SIZE[size] || data.DICE.d8
}

// Factory functions for adding new items
export { createMonster, defaultAttack, defaultTrait, defaultAction, defaultReaction, defaultSpeed }

// 5e Editor Template
export const dnd5eEditorTemplate = /*html*/`
<div class="dnd5e-editor">
  <!-- Basic Info Card -->
  <card>
    <div class="d-flex">
      <div class="flex-fill">
        <input class="form-control" v-model="target.name" placeholder="Monster Name">
      </div>
    </div>
    <row class="mt-2">
      <row-col>
        <div id="create-monster-actions" v-if="mode == 'edit'">
          <button class="btn btn-secondary text-uppercase mr-1" @click="saveToServer">Save</button>
          <a href="/creatures/my" class="text-muted" data-confirm="Are you sure you want to discard your edits?">Cancel</a>
        </div>
      </row-col>
    </row>
  </card>

  <!-- Core Stats Card -->
   <card header="Core Stats">
    <row>
      <row-col span="3">
        <div style="display: flex; align-items: center;">
          <input type="checkbox" id="swarm" v-model="target.swarm" style="margin-right: 6px;">
          <label for="swarm" style="margin-bottom: 0;">Swarm</label>
        </div>
        <div v-if="target.swarm" class="mt-2">
          <label>Swarm Size</label>
          <div class="swarm-size-checkboxes">
            <div v-for="s in dnd5eData.sizes" :key="'swarm-' + s" class="form-check" style="display:flex;align-items:center;padding-left:0;gap:0.25em">
              <input type="radio" class="form-check-input" style="position:static;margin:0" :id="'swarm-size-' + s" :value="s"
                v-model="target.swarmSize"
                name="swarmSize">
              <label class="form-check-label" :for="'swarm-size-' + s">{{ s }}</label>
            </div>
          </div>
        </div>
      </row-col>
      <row-col span="3">
        <label>Size <small class="text-muted">(max 2)</small></label>
        <div class="size-checkboxes">
          <div v-for="s in dnd5eData.sizes" :key="s" class="form-check" style="display:flex;align-items:center;padding-left:0;gap:0.25em">
            <input type="checkbox" class="form-check-input" style="position:static;margin:0" :id="'size-' + s" :value="s"
              :checked="isSizeSelected(s)"
              @change="toggleSize(s)"
              :disabled="isSizeDisabled(s)">
            <label class="form-check-label" :for="'size-' + s">{{ s }}</label>
          </div>
        </div>
      </row-col>
      <row-col span="6">
        <label>Type <small class="text-muted">(max 2)</small></label>
        <div class="type-checkboxes" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.25em;">
          <div v-for="t in dnd5eData.types" :key="t" class="form-check" style="display:flex;align-items:center;padding-left:0;gap:0.25em">
            <input type="checkbox" class="form-check-input" style="position:static;margin:0" :id="'type-' + t" :value="t"
              :checked="isTypeSelected(t)"
              @change="toggleType(t)"
              :disabled="isTypeDisabled(t)">
            <label class="form-check-label" :for="'type-' + t">{{ t }}</label>
          </div>
        </div>
      </row-col>
    </row>
    <row class="mt-2">
      <row-col span="12">
        <label>Descriptive Tag</label>
        <input class="form-control" v-model="target.descriptiveTag" placeholder="e.g., shapechanger, titan">
      </row-col>
    </row>
    <row class="mt-2">
      <row-col span="3">
        <label>Alignment</label>
        <select class="form-control" v-model="target.alignment">
          <option v-for="a in dnd5eData.alignments" :value="a">{{ a }}</option>
        </select>
      </row-col>
      <row-col span="3">
        <label>Challenge Rating</label>
        <select class="form-control" v-model="target.CR" @change="onCrChange">
          <option v-for="cr in dnd5eData.crOptions" :value="cr.value">{{ cr.label }}</option>
        </select>
      </row-col>
      <row-col span="3">
        <label>Armor Class</label>
        <input type="number" class="form-control" v-model.number="target.AC">
      </row-col>
      <row-col span="3">
        <label>Initiative</label>
        <div class="d-flex align-items-center">
          <input type="number" class="form-control" style="margin-right: 4px;"
            :value="getInitiativeMod()"
            @input="target.initiative.overrideValue = parseInt($event.target.value) || 0"
            :disabled="!target.initiative.override">
          <button type="button" class="btn btn-sm" style="padding: 2px 6px;"
            :class="target.initiative.override ? 'btn-warning' : 'btn-outline-secondary'"
            @click="toggleInitiativeOverride()"
            title="Override Value">
            <i class="fas fa-wrench" style="font-size: 10px;"></i>
          </button>
        </div>
      </row-col>
    </row>
    <row class="mt-2">
      <row-col span="3">
        <label>Hit Dice</label>
        <input type="number" class="form-control" v-model.number="target.HP.HD" min="1">
      </row-col>
      <row-col span="3">
        <label>Die Type</label>
        <select class="form-control" v-model.number="target.HP.type">
          <option v-for="d in dnd5eData.dice" :value="d.value">{{ d.label }}</option>
        </select>
      </row-col>
      <row-col span="3">
        <label>HP Modifier</label>
        <input type="number" class="form-control" v-model.number="target.HP.modifier">
      </row-col>
      <row-col span="3">
        <label>Avg HP</label>
        <div class="form-control bg-light d-flex align-items-center">{{ computedAvgHp }}</div>
      </row-col>
    </row>
    <row class="mt-2">
      <row-col span="3">
        <div style="display: flex; align-items: center;">
          <input type="checkbox" id="hasLair" v-model="target.hasLair" style="margin-right: 6px;">
          <label for="hasLair" style="margin-bottom: 0;">Has Lair?</label>
        </div>
      </row-col>
    </row>
    <row class="mt-2">
      <row-col>
        <label>Proficiency Bonus: +{{ computedProficiency }}</label>
        <small class="text-muted ml-2">(based on CR {{ target.CR }})</small>
      </row-col>
    </row>
  </card>

  <!-- Ability Scores Card -->
  <card header="Ability Scores">
    <row>
      <row-col v-for="stat in dnd5eData.stats" :key="stat" span="2" class="text-center">
        <label>{{ stat }}</label>
        <input type="number" class="form-control text-center" v-model.number="target.stats[stat]" min="1" max="30">
        <div class="text-muted small">{{ formatMod(getStatModifier(stat)) }}</div>
      </row-col>
    </row>
  </card>

  <!-- Saving Throws Card -->
  <card header="Saving Throws">
    <row>
      <row-col v-for="stat in dnd5eData.stats" :key="stat" span="4" class="mb-3">
        <label class="small text-muted">{{ stat }}</label>
        <div class="d-flex align-items-center">
          <input type="number" class="form-control form-control-sm" style="width: 50px; margin-right: 4px;"
            :value="getSaveMod(stat)"
            @input="target.saves[stat].overrideValue = parseInt($event.target.value) || 0"
            :disabled="!target.saves[stat].override">
          <button type="button" class="btn btn-sm" style="padding: 2px 6px; margin-right: 2px;"
            :class="target.saves[stat].proficient ? 'btn-primary' : 'btn-outline-secondary'"
            @click="toggleSaveProficiency(stat)"
            title="Toggle Proficiency">
            <i class="fas fa-chevron-up" style="font-size: 10px;"></i>
          </button>
          <button type="button" class="btn btn-sm" style="padding: 2px 6px;"
            :class="target.saves[stat].override ? 'btn-warning' : 'btn-outline-secondary'"
            @click="toggleSaveOverride(stat)"
            title="Override Value">
            <i class="fas fa-wrench" style="font-size: 10px;"></i>
          </button>
        </div>
      </row-col>
    </row>
  </card>

  <!-- Skills Card -->
  <card header="Skills">
    <row>
      <row-col v-for="(skill, key) in dnd5eData.skills" :key="key" span="6" class="mb-3">
        <label class="small text-muted">{{ skill.label }}</label>
        <div class="d-flex align-items-center">
          <input type="number" class="form-control form-control-sm" style="width: 50px; margin-right: 4px;"
            :value="getSkillMod(key)"
            @input="target.skills[key].overrideValue = parseInt($event.target.value) || 0"
            :disabled="!target.skills[key].override">
          <button type="button" class="btn btn-sm" style="padding: 2px 6px; margin-right: 2px;"
            :class="target.skills[key].proficient ? 'btn-primary' : 'btn-outline-secondary'"
            @click="toggleSkillProficiency(key)"
            title="Proficiency">
            <i class="fas fa-chevron-up" style="font-size: 10px;"></i>
          </button>
          <button type="button" class="btn btn-sm" style="padding: 2px 6px; margin-right: 2px;"
            :class="target.skills[key].expertise ? 'btn-primary' : 'btn-outline-secondary'"
            @click="toggleSkillExpertise(key)"
            title="Expertise">
            <i class="fas fa-angle-double-up" style="font-size: 10px;"></i>
          </button>
          <button type="button" class="btn btn-sm" style="padding: 2px 6px;"
            :class="target.skills[key].override ? 'btn-warning' : 'btn-outline-secondary'"
            @click="toggleSkillOverride(key)"
            title="Override Value">
            <i class="fas fa-wrench" style="font-size: 10px;"></i>
          </button>
        </div>
      </row-col>
    </row>
  </card>

  <!-- Movement Card -->
  <card header="Movement">
    <div v-for="(spd, idx) in target.speeds" :key="spd.id" class="mb-2">
      <row>
        <row-col span="3">
          <select class="form-control form-control-sm" v-model="spd.type">
            <option v-for="mt in dnd5eData.movementTypes" :value="mt">{{ mt }}</option>
          </select>
        </row-col>
        <row-col span="3">
          <div class="input-group input-group-sm flex-nowrap">
            <input type="number" class="form-control" v-model.number="spd.speed" min="0" step="5">
            <div class="input-group-append">
              <span class="input-group-text py-0" style="font-size: 0.7rem;">ft.</span>
            </div>
          </div>
        </row-col>
        <row-col span="4">
          <input class="form-control form-control-sm" v-model="spd.note" placeholder="notes (e.g., hover)">
        </row-col>
        <row-col span="2">
          <button class="btn btn-sm btn-outline-danger" @click="removeSpeed(idx)" v-if="target.speeds.length > 1">
            <i class="fas fa-times"></i>
          </button>
        </row-col>
      </row>
    </div>
    <button class="btn btn-sm btn-outline-secondary px-2" @click="addSpeed">
      <i class="fas fa-plus"></i> Add Speed
    </button>
  </card>

  <!-- Gear Card -->
  <card header="Gear">
    <row>
      <row-col>
        <textarea class="form-control" v-model="target.gear" rows="3" placeholder="Gear (markdown supported)"></textarea>
      </row-col>
    </row>
  </card>

  <!-- Senses Card -->
  <card header="Senses">
    <row>
      <row-col span="3">
        <label>Blindsight</label>
        <div class="input-group input-group-sm flex-nowrap">
          <input type="number" class="form-control" v-model.number="target.blindsight" min="0" step="5">
          <div class="input-group-append"><span class="input-group-text py-0" style="font-size: 0.7rem;">ft.</span></div>
        </div>
      </row-col>
      <row-col span="3">
        <label>Darkvision</label>
        <div class="input-group input-group-sm flex-nowrap">
          <input type="number" class="form-control" v-model.number="target.darkvision" min="0" step="30">
          <div class="input-group-append"><span class="input-group-text py-0" style="font-size: 0.7rem;">ft.</span></div>
        </div>
      </row-col>
      <row-col span="3">
        <label>Tremorsense</label>
        <div class="input-group input-group-sm flex-nowrap">
          <input type="number" class="form-control" v-model.number="target.tremorsense" min="0" step="5">
          <div class="input-group-append"><span class="input-group-text py-0" style="font-size: 0.7rem;">ft.</span></div>
        </div>
      </row-col>
      <row-col span="3">
        <label>Truesight</label>
        <div class="input-group input-group-sm flex-nowrap">
          <input type="number" class="form-control" v-model.number="target.truesight" min="0" step="5">
          <div class="input-group-append"><span class="input-group-text py-0" style="font-size: 0.7rem;">ft.</span></div>
        </div>
      </row-col>
    </row>
    <row class="mt-2">
      <row-col>
        <label>Passive Perception: {{ computedPassivePerception }}</label>
      </row-col>
    </row>
  </card>

  <!-- Languages Card -->
  <card header="Languages">
    <row>
      <row-col span="9">
        <label>Languages</label>
        <input class="form-control form-control-sm" v-model="target.languages" placeholder="Common, Draconic, etc.">
      </row-col>
      <row-col span="3">
        <label>Telepathy</label>
        <div class="input-group input-group-sm flex-nowrap">
          <input type="number" class="form-control" v-model.number="target.telepathy" min="0" step="30">
          <div class="input-group-append"><span class="input-group-text py-0" style="font-size: 0.7rem;">ft.</span></div>
        </div>
      </row-col>
    </row>
  </card>

  <!-- Damage Resistances/Immunities/Vulnerabilities Card -->
  <card header="Damage Modifiers">
    <row>
      <row-col span="4">
        <label>Vulnerabilities</label>
        <div v-for="dt in dnd5eData.damageTypes" :key="'vuln-' + dt" class="form-check" style="display:flex;align-items:center;padding-left:0;gap:0.25em">
          <input type="checkbox" class="form-check-input" style="position:static;margin:0" :id="'vuln-' + dt" :value="dt" v-model="target.vulnerabilities">
          <label class="form-check-label" :for="'vuln-' + dt">{{ dt }}</label>
        </div>
      </row-col>
      <row-col span="4">
        <label>Resistances</label>
        <div v-for="dt in dnd5eData.damageTypes" :key="'res-' + dt" class="form-check" style="display:flex;align-items:center;padding-left:0;gap:0.25em">
          <input type="checkbox" class="form-check-input" style="position:static;margin:0" :id="'res-' + dt" :value="dt" v-model="target.resistances">
          <label class="form-check-label" :for="'res-' + dt">{{ dt }}</label>
        </div>
      </row-col>
      <row-col span="4">
        <label>Immunities</label>
        <div v-for="dt in dnd5eData.damageTypes" :key="'imm-' + dt" class="form-check" style="display:flex;align-items:center;padding-left:0;gap:0.25em">
          <input type="checkbox" class="form-check-input" style="position:static;margin:0" :id="'imm-' + dt" :value="dt" v-model="target.immunities">
          <label class="form-check-label" :for="'imm-' + dt">{{ dt }}</label>
        </div>
      </row-col>
    </row>
  </card>

  <!-- Condition Immunities Card -->
  <card header="Condition Immunities">
    <row>
      <row-col v-for="cond in dnd5eData.conditions" :key="cond" span="4" class="mb-1">
        <div class="form-check" style="display:flex;align-items:center;padding-left:0;gap:0.25em">
          <input type="checkbox" class="form-check-input" style="position:static;margin:0" :id="'cond-' + cond" :value="cond" v-model="target.conditionImmunities">
          <label class="form-check-label" :for="'cond-' + cond">{{ cond }}</label>
        </div>
      </row-col>
    </row>
  </card>

  <!-- Traits Card -->
  <card header="Traits">
    <div v-for="(trait, idx) in target.traits" :key="trait.id" class="mb-3 p-2 border rounded">
      <row>
        <row-col span="10">
          <input class="form-control" v-model="trait.name" placeholder="Trait Name">
        </row-col>
        <row-col span="2" class="d-flex align-items-center justify-content-end">
          <button class="btn btn-outline-danger px-2 d-flex align-items-center justify-content-center" style="height: calc(1.5em + .25rem); font-size: .875rem;" @click="removeTrait(idx)">
            <i class="fas fa-times"></i>
          </button>
        </row-col>
      </row>
      <row class="mt-2">
        <row-col>
          <textarea class="form-control" v-model="trait.description" rows="3" placeholder="Trait description..."></textarea>
        </row-col>
      </row>
    </div>
    <button class="btn btn-sm btn-outline-secondary px-2" @click="addTrait">
      <i class="fas fa-plus"></i> Add Trait
    </button>
  </card>

  <!-- Attacks Card -->
  <card header="Attacks">
    <row class="mb-2">
      <row-col>
        <label>Multiattack</label>
        <input class="form-control" v-model="target.multiattack" placeholder="Multiattack description">
      </row-col>
    </row>
    <div v-for="(attack, idx) in target.attacks" :key="attack.id" class="mb-3 p-2 border rounded">
      <row>
        <row-col span="4">
          <label>Name</label>
          <input class="form-control" v-model="attack.name" placeholder="Attack Name">
        </row-col>
        <row-col span="2">
          <label>Type</label>
          <select class="form-control" v-model="attack.attackType">
            <option v-for="at in dnd5eData.attackTypes" :value="at">{{ at }}</option>
          </select>
        </row-col>
        <row-col span="2">
          <label>Reach</label>
          <input type="number" class="form-control" v-model.number="attack.reach" min="0" step="5">
        </row-col>
        <row-col span="2" class="d-flex flex-column">
          <label>&nbsp;</label>
          <div class="d-flex justify-content-end">
            <button class="btn btn-outline-danger px-2 d-flex align-items-center justify-content-center" style="height: calc(1.5em + .25rem); font-size: .875rem;" @click="removeAttack(idx)">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </row-col>
      </row>
      <row class="mt-2">
        <row-col span="3">
          <label>Normal Range</label>
          <div class="input-group input-group-sm flex-nowrap">
            <input type="number" class="form-control" v-model.number="attack.range" min="0" step="5">
            <div class="input-group-append"><span class="input-group-text py-0" style="font-size: 0.7rem;">ft.</span></div>
          </div>
        </row-col>
        <row-col span="3">
          <label>Long Range</label>
          <div class="input-group input-group-sm flex-nowrap">
            <input type="number" class="form-control" v-model.number="attack.longRange" min="0" step="5">
            <div class="input-group-append"><span class="input-group-text py-0" style="font-size: 0.7rem;">ft.</span></div>
          </div>
        </row-col>
      </row>
      <row class="mt-2">
        <row-col span="2">
          <label>To Hit Stat</label>
          <select class="form-control form-control-sm" v-model="attack.modifier.stat">
            <option v-for="s in dnd5eData.stats" :value="s">{{ s }}</option>
          </select>
        </row-col>
        <row-col span="2">
          <label>To Hit</label>
          <input type="number" class="form-control form-control-sm mb-1"
            :value="getAttackMod(attack)"
            @input="attack.modifier.overrideValue = parseInt($event.target.value) || 0"
            :disabled="!attack.modifier.override">
          <div class="d-flex">
            <button type="button" class="btn btn-sm flex-fill mr-1" style="padding: 2px 6px;"
              :class="attack.modifier.proficient ? 'btn-primary' : 'btn-outline-secondary'"
              @click="attack.modifier.proficient = !attack.modifier.proficient"
              title="Toggle Proficiency">
              <i class="fas fa-chevron-up" style="font-size: 10px;"></i>
            </button>
            <button type="button" class="btn btn-sm flex-fill" style="padding: 2px 6px;"
              :class="attack.modifier.override ? 'btn-warning' : 'btn-outline-secondary'"
              @click="toggleAttackOverride(attack)"
              title="Override Value">
              <i class="fas fa-wrench" style="font-size: 10px;"></i>
            </button>
          </div>
        </row-col>
        <row-col span="2">
          <label>Damage Dice</label>
          <input type="number" class="form-control" v-model.number="attack.damage.count" min="1">
        </row-col>
        <row-col span="2">
          <label>Die</label>
          <select class="form-control" v-model.number="attack.damage.die">
            <option v-for="d in dnd5eData.dice" :value="d.value">{{ d.label }}</option>
          </select>
        </row-col>
        <row-col span="2">
          <label>Dmg Stat</label>
          <select class="form-control" v-model="attack.damage.modifier.stat">
            <option value="">None</option>
            <option v-for="s in dnd5eData.stats" :value="s">{{ s }}</option>
          </select>
        </row-col>
        <row-col span="2">
          <label>Dmg Type</label>
          <select class="form-control" v-model="attack.damage.type">
            <option v-for="dt in dnd5eData.damageTypes" :value="dt">{{ dt }}</option>
          </select>
        </row-col>
      </row>
      <row class="mt-2">
        <row-col>
          <label>Additional Description</label>
          <textarea class="form-control" v-model="attack.description" rows="2" placeholder="Additional effects..."></textarea>
        </row-col>
      </row>
    </div>
    <button class="btn btn-sm btn-outline-secondary px-2" @click="addAttack">
      <i class="fas fa-plus"></i> Add Attack
    </button>
  </card>

  <!-- Actions Card -->
  <card header="Actions">
    <div v-for="(action, idx) in target.actions" :key="action.id" class="mb-3 p-2 border rounded">
      <row>
        <row-col span="6">
          <input class="form-control" v-model="action.name" placeholder="Action Name">
        </row-col>
        <row-col span="4">
          <select class="form-control" v-model="action.recharge">
            <option v-for="r in dnd5eData.rechargeOptions" :value="r.value">{{ r.label }}</option>
          </select>
        </row-col>
        <row-col span="2" class="d-flex align-items-center justify-content-end">
          <button class="btn btn-outline-danger px-2 d-flex align-items-center justify-content-center" style="height: calc(1.5em + .25rem); font-size: .875rem;" @click="removeAction(idx)">
            <i class="fas fa-times"></i>
          </button>
        </row-col>
      </row>
      <row class="mt-2">
        <row-col>
          <textarea class="form-control" v-model="action.description" rows="3" placeholder="Action description..."></textarea>
        </row-col>
      </row>
    </div>
    <button class="btn btn-sm btn-outline-secondary px-2" @click="addAction">
      <i class="fas fa-plus"></i> Add Action
    </button>
  </card>

  <!-- Bonus Actions Card -->
  <card header="Bonus Actions">
    <div v-for="(bonusAction, idx) in target.bonusActions" :key="bonusAction.id" class="mb-3 p-2 border rounded">
      <row>
        <row-col span="6">
          <input class="form-control" v-model="bonusAction.name" placeholder="Bonus Action Name">
        </row-col>
        <row-col span="4">
          <select class="form-control" v-model="bonusAction.recharge">
            <option v-for="r in dnd5eData.rechargeOptions" :value="r.value">{{ r.label }}</option>
          </select>
        </row-col>
        <row-col span="2" class="d-flex align-items-center justify-content-end">
          <button class="btn btn-outline-danger px-2 d-flex align-items-center justify-content-center" style="height: calc(1.5em + .25rem); font-size: .875rem;" @click="removeBonusAction(idx)">
            <i class="fas fa-times"></i>
          </button>
        </row-col>
      </row>
      <row class="mt-2">
        <row-col>
          <textarea class="form-control" v-model="bonusAction.description" rows="3" placeholder="Bonus action description..."></textarea>
        </row-col>
      </row>
    </div>
    <button class="btn btn-sm btn-outline-secondary px-2" @click="addBonusAction">
      <i class="fas fa-plus"></i> Add Bonus Action
    </button>
  </card>

  <!-- Reactions Card -->
  <card header="Reactions">
    <div v-for="(reaction, idx) in target.reactions" :key="reaction.id" class="mb-3 p-2 border rounded">
      <row>
        <row-col span="6">
          <input class="form-control" v-model="reaction.name" placeholder="Reaction Name">
        </row-col>
        <row-col span="4">
          <select class="form-control" v-model="reaction.recharge">
            <option v-for="r in dnd5eData.rechargeOptions" :value="r.value">{{ r.label }}</option>
          </select>
        </row-col>
        <row-col span="2" class="d-flex align-items-center justify-content-end">
          <button class="btn btn-outline-danger px-2 d-flex align-items-center justify-content-center" style="height: calc(1.5em + .25rem); font-size: .875rem;" @click="removeReaction(idx)">
            <i class="fas fa-times"></i>
          </button>
        </row-col>
      </row>
      <row class="mt-2">
        <row-col>
          <textarea class="form-control" v-model="reaction.description" rows="3" placeholder="Reaction description..."></textarea>
        </row-col>
      </row>
    </div>
    <button class="btn btn-sm btn-outline-secondary px-2" @click="addReaction">
      <i class="fas fa-plus"></i> Add Reaction
    </button>
  </card>

  <!-- Legendary Actions Card -->
  <card header="Legendary Actions">
    <row class="mb-2">
      <row-col>
        <label>Preamble</label>
        <textarea class="form-control" v-model="target.legendaryActions.preamble" rows="3"
          @input="target.legendaryActions.preambleEdited = true"
          placeholder="Legendary Action Uses: 3 (4 in Lair). Immediately after another creature's turn..."></textarea>
      </row-col>
    </row>
    <div v-for="(la, idx) in target.legendaryActions.actions" :key="la.id" class="mb-2 p-2 border rounded">
      <row class="align-items-end">
        <row-col span="9">
          <label>Name</label>
          <input class="form-control form-control-sm" v-model="la.name" placeholder="Legendary Action Name">
        </row-col>
        <row-col span="1" class="d-flex align-items-end justify-content-end">
          <button class="btn btn-outline-danger px-2 d-flex align-items-center justify-content-center" style="height: calc(1.5em + .25rem); font-size: .875rem;" @click="removeLegendaryAction(idx)">
            <i class="fas fa-times"></i>
          </button>
        </row-col>
      </row>
      <row class="mt-2">
        <row-col>
          <textarea class="form-control" v-model="la.description" rows="3" placeholder="Legendary action description..."></textarea>
        </row-col>
      </row>
    </div>
    <button class="btn btn-sm btn-outline-secondary px-2" @click="addLegendaryAction">
      <i class="fas fa-plus"></i> Add Legendary Action
    </button>
  </card>

  <!-- Description Card -->
  <card header="Description">
    <textarea class="form-control" v-model="target.description" rows="6"
      placeholder="Monster description, lore, and background..."></textarea>
    <label class="mt-2">Art Direction <small class="text-muted">(only used for RFC Monster Master entries, not required)</small></label>
    <textarea class="form-control" v-model="target.artdirection" rows="5"
      placeholder="Art direction..."></textarea>
  </card>
</div>
`

// 5e Editor methods to mix into the main Vue component
export const dnd5eMethods = {
  // Size selection methods
  isSizeSelected(size) {
    if (!Array.isArray(this.target.size)) return false
    return this.target.size.includes(size)
  },

  isSizeDisabled(size) {
    if (!Array.isArray(this.target.size)) return false
    return !this.target.size.includes(size) && this.target.size.length >= 2
  },

  toggleSize(size) {
    // Ensure size is an array (backward compatibility)
    if (!Array.isArray(this.target.size)) {
      this.target.size = this.target.size ? [this.target.size] : []
    }
    const idx = this.target.size.indexOf(size)
    if (idx > -1) {
      // Remove if already selected (but keep at least one)
      if (this.target.size.length > 1) {
        // Replace array to ensure Vue reactivity
        this.target.size = this.target.size.filter((_, i) => i !== idx)
      }
    } else if (this.target.size.length < 2) {
      // Add if under limit - replace array to ensure Vue reactivity
      this.target.size = [...this.target.size, size]
    }
    // Sync checkbox DOM state with data after Vue updates
    this.$nextTick(() => {
      document.querySelectorAll('[id^="size-"]').forEach(cb => {
        cb.checked = this.target.size.includes(cb.value)
        cb.disabled = !this.target.size.includes(cb.value) && this.target.size.length >= 2
      })
    })
  },

  sortSizes(sizes) {
    const sizeOrder = data.CREATURE_SIZE
    return sizes.sort((a, b) => sizeOrder.indexOf(a) - sizeOrder.indexOf(b))
  },

  getSizeDisplay() {
    if (this.target.swarm) {
      const swarmSize = this.target.swarmSize || 'Medium'
      let typeDisplay
      if (!this.target.type) {
        typeDisplay = 'Humanoid'
      } else if (Array.isArray(this.target.type)) {
        typeDisplay = this.target.type.join(' or ')
      } else {
        typeDisplay = this.target.type
      }
      const pluralizedType = this.pluralizeType(typeDisplay)
      // Get the creature size(s) for display
      let creatureSize = 'Medium'
      if (this.target.size) {
        if (Array.isArray(this.target.size)) {
          creatureSize = this.sortSizes([...this.target.size]).join(' or ')
        } else {
          creatureSize = this.target.size
        }
      }
      return `${swarmSize} Swarm of ${creatureSize} ${pluralizedType}`
    }
    if (!this.target.size) return 'Medium'
    // Handle both string (old data) and array (new data)
    if (Array.isArray(this.target.size)) {
      return this.sortSizes([...this.target.size]).join(' or ')
    }
    return this.target.size
  },

  pluralizeType(type) {
    if (!type) return 'Creatures'
    const typeLower = type.toLowerCase()
    const irregularPlurals = {
      'beast': 'beasts',
      'celestial': 'celestials',
      'construct': 'constructs',
      'dragon': 'dragons',
      'elemental': 'elementals',
      'fey': 'fey',
      'fiend': 'fiends',
      'giant': 'giants',
      'humanoid': 'humanoids',
      'monstrosity': 'monstrosities',
      'ooze': 'oozes',
      'plant': 'plants',
      'aberration': 'aberrations',
      'undead': 'undead',
    }
    // Handle "or" separator
    if (typeLower.includes(' or ')) {
      const types = typeLower.split(' or ')
      const pluralized = types.map(t => irregularPlurals[t] || t + 's')
      return pluralized.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' or ')
    }
    const result = irregularPlurals[typeLower] || typeLower + 's'
    return result.charAt(0).toUpperCase() + result.slice(1)
  },

  // Type selection methods
  isTypeSelected(type) {
    if (!Array.isArray(this.target.type)) return false
    return this.target.type.includes(type)
  },

  isTypeDisabled(type) {
    if (!Array.isArray(this.target.type)) return false
    return !this.target.type.includes(type) && this.target.type.length >= 2
  },

  toggleType(type) {
    // Ensure type is an array (backward compatibility)
    if (!Array.isArray(this.target.type)) {
      this.target.type = this.target.type ? [this.target.type] : []
    }
    const idx = this.target.type.indexOf(type)
    if (idx > -1) {
      // Remove if already selected (but keep at least one)
      if (this.target.type.length > 1) {
        // Replace array to ensure Vue reactivity
        this.target.type = this.target.type.filter((_, i) => i !== idx)
      }
    } else if (this.target.type.length < 2) {
      // Add if under limit - replace array to ensure Vue reactivity
      this.target.type = [...this.target.type, type]
    }
    // Sync checkbox DOM state with data after Vue updates
    this.$nextTick(() => {
      document.querySelectorAll('[id^="type-"]').forEach(cb => {
        cb.checked = this.target.type.includes(cb.value)
        cb.disabled = !this.target.type.includes(cb.value) && this.target.type.length >= 2
      })
    })
  },

  getTypeDisplay() {
    if (this.target.swarm) return ''
    if (!this.target.type) return 'Humanoid'
    // Handle both string (old data) and array (new data)
    if (Array.isArray(this.target.type)) {
      return this.target.type.join(' or ')
    }
    return this.target.type
  },

  // Computed-like methods for 5e
  getStatModifier(stat) {
    return getModifier(this.target.stats[stat])
  },

  formatMod(mod) {
    return formatModifier(mod)
  },

  getSaveBonus(stat) {
    if (this.target.saves[stat].override) {
      return formatModifier(this.target.saves[stat].overrideValue)
    }
    const mod = this.getStatModifier(stat)
    const profBonus = this.target.saves[stat].proficient ? this.computedProficiency : 0
    return formatModifier(mod + profBonus)
  },

  getSaveMod(stat) {
    if (this.target.saves[stat].override) {
      return this.target.saves[stat].overrideValue
    }
    const mod = this.getStatModifier(stat)
    const profBonus = this.target.saves[stat].proficient ? this.computedProficiency : 0
    return mod + profBonus
  },

  toggleSaveProficiency(stat) {
    this.target.saves[stat].proficient = !this.target.saves[stat].proficient
  },

  toggleSaveOverride(stat) {
    const save = this.target.saves[stat]
    save.override = !save.override
    // When enabling override, initialize with calculated value
    if (save.override) {
      const mod = this.getStatModifier(stat)
      const profBonus = save.proficient ? this.computedProficiency : 0
      save.overrideValue = mod + profBonus
    }
  },

  getSkillBonus(skillKey) {
    if (this.target.skills[skillKey].override) {
      return formatModifier(this.target.skills[skillKey].overrideValue)
    }
    const skill = dnd5eData.skills[skillKey]
    const mod = this.getStatModifier(skill.stat)
    let bonus = mod
    if (this.target.skills[skillKey].proficient) {
      bonus += this.computedProficiency
    }
    if (this.target.skills[skillKey].expertise) {
      bonus += this.computedProficiency
    }
    return formatModifier(bonus)
  },

  getSkillMod(skillKey) {
    if (this.target.skills[skillKey].override) {
      return this.target.skills[skillKey].overrideValue
    }
    const skill = dnd5eData.skills[skillKey]
    const mod = this.getStatModifier(skill.stat)
    let bonus = mod
    if (this.target.skills[skillKey].proficient) {
      bonus += this.computedProficiency
    }
    if (this.target.skills[skillKey].expertise) {
      bonus += this.computedProficiency
    }
    return bonus
  },

  toggleSkillProficiency(skillKey) {
    this.target.skills[skillKey].proficient = !this.target.skills[skillKey].proficient
    // If turning off proficiency, also turn off expertise
    if (!this.target.skills[skillKey].proficient) {
      this.target.skills[skillKey].expertise = false
    }
  },

  toggleSkillExpertise(skillKey) {
    // Can only enable expertise if proficient
    if (!this.target.skills[skillKey].proficient) {
      // Enable proficiency first, then expertise
      this.target.skills[skillKey].proficient = true
    }
    this.target.skills[skillKey].expertise = !this.target.skills[skillKey].expertise
  },

  toggleSkillOverride(skillKey) {
    const skill = this.target.skills[skillKey]
    // Calculate the value before toggling override
    const calculatedValue = this.getSkillMod(skillKey)
    skill.override = !skill.override
    // When enabling override, initialize with calculated value
    if (skill.override) {
      skill.overrideValue = calculatedValue
    }
  },

  getAttackBonus(attack) {
    if (attack.modifier.override) {
      return formatModifier(attack.modifier.overrideValue)
    }
    const mod = this.getStatModifier(attack.modifier.stat)
    const profBonus = attack.modifier.proficient ? this.computedProficiency : 0
    return formatModifier(mod + profBonus)
  },

  getAttackMod(attack) {
    if (attack.modifier.override) {
      return attack.modifier.overrideValue
    }
    const mod = this.getStatModifier(attack.modifier.stat)
    const profBonus = attack.modifier.proficient ? this.computedProficiency : 0
    return mod + profBonus
  },

  toggleAttackOverride(attack) {
    attack.modifier.override = !attack.modifier.override
    // When enabling override, initialize with calculated value
    if (attack.modifier.override) {
      const mod = this.getStatModifier(attack.modifier.stat)
      const profBonus = attack.modifier.proficient ? this.computedProficiency : 0
      attack.modifier.overrideValue = mod + profBonus
    }
  },

  onCrChange() {
    const crData = getCrData(this.target.CR)
    if (crData) {
      this.target.proficiency = crData.proficiency
    }
  },

  getInitiativeMod() {
    if (this.target.initiative.override) {
      return this.target.initiative.overrideValue
    }
    return this.getStatModifier('DEX')
  },

  toggleInitiativeOverride() {
    this.target.initiative.override = !this.target.initiative.override
    if (this.target.initiative.override) {
      this.target.initiative.overrideValue = this.getStatModifier('DEX')
    }
  },

  // Add/Remove methods for 5e
  addSpeed() {
    this.target.speeds.push(defaultSpeed('fly'))
  },

  removeSpeed(idx) {
    this.target.speeds.splice(idx, 1)
  },

  addTrait() {
    this.target.traits.push(defaultTrait())
  },

  removeTrait(idx) {
    this.target.traits.splice(idx, 1)
  },

  addAction() {
    this.target.actions.push(defaultAction())
  },

  removeAction(idx) {
    this.target.actions.splice(idx, 1)
  },

  addAttack() {
    this.target.attacks.push(defaultAttack())
  },

  removeAttack(idx) {
    this.target.attacks.splice(idx, 1)
  },

  addBonusAction() {
    this.target.bonusActions.push(defaultBonusAction())
  },

  removeBonusAction(idx) {
    this.target.bonusActions.splice(idx, 1)
  },

  addReaction() {
    this.target.reactions.push(defaultReaction())
  },

  removeReaction(idx) {
    this.target.reactions.splice(idx, 1)
  },

  addLegendaryAction() {
    this.target.legendaryActions.actions.push({
      id: '_' + Date.now().toString(36),
      name: '',
      description: '',
    })
  },

  removeLegendaryAction(idx) {
    this.target.legendaryActions.actions.splice(idx, 1)
  },

  getLegendaryPreambleText() {
    const creatureName = this.target.name || 'creature'
    return `Legendary Action Uses: 3 (4 in Lair). Immediately after another creature's turn, ${creatureName} can expend a use to take one of the following actions. ${creatureName} regains all expended uses at the start of each of its turns.`
  },

  updateLegendaryPreamble() {
    if (!this.target.legendaryActions.preambleEdited) {
      this.target.legendaryActions.preamble = this.getLegendaryPreambleText()
    }
  },

  // Stat block helper methods
  getDieName(dieValue) {
    return getDieName(dieValue)
  },

  getXpForCr(cr) {
    const crData = getCrData(cr)
    return crData ? crData.xp.toLocaleString() : '0'
  },

  getLairXpForCr(cr) {
    const crData = getCrData(cr)
    if (!crData) return '0'
    const nextCr = data.CR_DATA[crData.index + 1]
    return nextCr ? nextCr.xp.toLocaleString() : crData.xp.toLocaleString()
  },

  getSavingThrowsText() {
    if (this.system !== 'dnd5e') return ''
    const saves = []
    for (const stat of data.STATS) {
      if (this.target.saves[stat].proficient || this.target.saves[stat].override) {
        saves.push(`${stat} ${this.getSaveBonus(stat)}`)
      }
    }
    return saves.join(', ')
  },

  getSkillsText() {
    if (this.system !== 'dnd5e') return ''
    const skills = []
    for (const [key, skill] of Object.entries(data.SKILLS)) {
      if (this.target.skills[key].proficient) {
        skills.push(`${skill.label} ${this.getSkillBonus(key)}`)
      }
    }
    return skills.join(', ')
  },

  getAttackDamage(attack) {
    const avgDamage = data.avgRoll(attack.damage.count, attack.damage.die)
    let mod = 0
    if (attack.damage.modifier.stat) {
      mod = this.getStatModifier(attack.damage.modifier.stat)
    }
    const total = avgDamage + mod
    const dieStr = getDieName(attack.damage.die)
    const modStr = mod !== 0 ? (mod >= 0 ? ` + ${mod}` : ` - ${Math.abs(mod)}`) : ''
    return `${total} (${attack.damage.count}${dieStr}${modStr})`
  },

  getAttackHeader(attack) {
    if (attack.attackType === 'Melee or Ranged') {
      return 'Melee or Ranged Attack Roll:'
    }
    return `${attack.attackType} Weapon Attack Roll:`
  },

  getAttackReachRange(attack) {
    const reach = attack.reach || 5
    const range = attack.range || 0
    const longRange = attack.longRange || 0

    // Format range string based on whether long range exists
    const formatRange = (normalRange, longR) => {
      if (longR > 0) {
        return `range ${normalRange}/${longR} ft.`
      }
      return `range ${normalRange} ft.`
    }

    if (attack.attackType === 'Melee or Ranged') {
      return `reach ${reach} ft. or ${formatRange(range, longRange)}`
    }
    // For ranged attacks, show range
    if (attack.attackType === 'Ranged') {
      return formatRange(range || reach, longRange)
    }
    // For melee attacks, show reach
    return `reach ${reach} ft.`
  },

  formatRecharge(recharge) {
    const map = {
      'RECHARGE_6': 'Recharge 6',
      'RECHARGE_5_6': 'Recharge 5-6',
      'RECHARGE_4_6': 'Recharge 4-6',
      'SHORT_REST': 'Recharges after a Short or Long Rest',
      'LONG_REST': 'Recharges after a Long Rest',
      '1_DAY': '1/Day',
      '2_DAY': '2/Day',
      '3_DAY': '3/Day',
    }
    return map[recharge] || recharge
  },
}

// 5e computed properties to mix into the main Vue component
export const dnd5eComputed = {
  computedProficiency() {
    if (this.system !== 'dnd5e') return 0
    const crData = getCrData(this.target.CR)
    return crData ? crData.proficiency : 2
  },

  computedAvgHp() {
    if (this.system !== 'dnd5e') return 0
    return getAvgHp(this.target.HP.HD, this.target.HP.type, this.target.HP.modifier)
  },

  computedInitiative() {
    if (this.system !== 'dnd5e') return 0
    if (this.target.initiative.override) {
      return this.target.initiative.overrideValue
    }
    return getModifier(this.target.stats.DEX)
  },

  computedPassivePerception() {
    if (this.system !== 'dnd5e') return 10
    const wisMod = getModifier(this.target.stats.WIS)
    let bonus = wisMod
    if (this.target.skills.PERCEPTION.proficient) {
      bonus += this.computedProficiency
    }
    if (this.target.skills.PERCEPTION.expertise) {
      bonus += this.computedProficiency
    }
    return 10 + bonus
  },
}
